/**
 * Browser client for E.V.A — the Electromotive Virtual Analyst (`/api/eva/ask`).
 *
 * Retrieval happens in the browser (see knowledgeBase.ts) so answers respect the
 * signed-in user's own SharePoint access. This module only ships the question +
 * pre-gathered context to the reasoning endpoint and returns the grounded answer.
 */

import { getApiTokenSilent } from "../../shell/auth/tokens";
import { apiAuthHeaders } from "../../lib/apiAuthHeaders";

export interface EvaTurn {
  role: "user" | "assistant";
  content: string;
}

export interface EvaAnswer {
  answer: string;
  followups: string[];
  provider?: string;
}

async function authHeaders(): Promise<Record<string, string>> {
  return apiAuthHeaders(await getApiTokenSilent());
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body.error || `Request failed (${res.status}).`;
  } catch {
    return `Request failed (${res.status}).`;
  }
}

/**
 * Ask E.V.A a question, grounded in `context` (compact JSON of the rows the
 * knowledge base retrieved for this question). `history` is the recent
 * conversation for follow-up continuity.
 */
export async function askEva(
  question: string,
  context: string,
  history: EvaTurn[] = [],
  asOf?: string
): Promise<EvaAnswer> {
  const res = await fetch("/api/eva/ask", {
    method: "POST",
    headers: { ...(await authHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({ question, context, history, asOf }),
  });
  if (!res.ok) throw new Error(await readError(res));
  const data = (await res.json()) as Partial<EvaAnswer>;
  return {
    answer: data.answer ?? "",
    followups: Array.isArray(data.followups) ? data.followups : [],
    provider: data.provider,
  };
}
